import type { ReactNode } from 'react';
import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react';
import { AdminLayout } from '@/layouts/AdminLayout';
import { BareLayout } from '@/layouts/BareLayout';
import { PublicLayout } from '@/layouts/PublicLayout';

const pages = import.meta.glob<{ default: ResolvedComponent }>('../pages/**/*.tsx', {
  eager: true,
});

void createInertiaApp({
  resolve: (name) => {
    const page = pages[`../pages/${name}.tsx`];

    // Assign a default layout by path, unless the page already opted into
    // its own layout (`Page.layout = ...`) — that per-page override wins.
    page.default.layout ??=
      name === 'Admin/Login'
        ? (p: ReactNode) => <BareLayout>{p}</BareLayout>
        : name.startsWith('Admin/')
          ? (p: ReactNode) => <AdminLayout>{p}</AdminLayout>
          : (p: ReactNode) => <PublicLayout>{p}</PublicLayout>;

    return page;
  },

  strictMode: true,

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: 'brackets' };
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById('app')) {
    throw error;
  } else {
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    );
  }
});
